import React from "react";
import {
  TableCell,
  TableRow,
  Typography,
  Link,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { format } from "date-fns";
import { convertTokens } from "@date-fns/upgrade/v2";
import { CalendarDots, CalendarX, DotsThree, DownloadSimple, Play, Target } from "@phosphor-icons/react";
import { capitalizeBillingTypeFirstLetter } from "../../../../../utils/capitalizeBillingTypeFirstLetter";
import { VideoCall } from "./types";
import { findDifferenceInMinutes } from "utils/findDifferenceInMinutes";

interface CallTableRowProps {
  videoCall: VideoCall;
  anchorEl: HTMLElement | null;
  onOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseMenu: () => void;
  onDownloadNotes: () => void;
  onWatchVideo: () => void;
  onViewDetails: () => void;
  onOpenDataSheet?: () => void;
  onReschedule?: () => void;
  onCancel?: () => void;
  userPermissions?: any;
  currentTab: "upcoming" | "past";
  canModifyEvent?: boolean;
  currentUserAssignedToCall?: boolean;
}

export const CallTableRow: React.FC<CallTableRowProps> = ({
  videoCall,
  anchorEl,
  onOpenMenu,
  onCloseMenu,
  onDownloadNotes,
  onWatchVideo,
  onViewDetails,
  onOpenDataSheet,
  onReschedule,
  onCancel,
  userPermissions,
  currentTab,
  canModifyEvent,
  currentUserAssignedToCall,
}) => (
  <TableRow key={videoCall.id}>
    <TableCell align="left" sx={{ p: 5 }}>
      <Typography variant="body2">
        {videoCall.start_date
          ? format(new Date(videoCall.start_date), convertTokens("MMM D, YYYY h:mm a"))
          : videoCall.scheduled_date
            ? format(new Date(videoCall.scheduled_date), convertTokens("MMMM Do h:mm a"))
            : "-"}
      </Typography>
    </TableCell>
    <TableCell align="left" sx={{ p: 5 }}>
      <Typography variant="body2">
        {videoCall.end_date
          ? format(new Date(videoCall.end_date), convertTokens("MMM D, YYYY h:mm a"))
          : "-"}
      </Typography>
    </TableCell>
    <TableCell align="left" sx={{ p: 5 }}>
      <Typography variant="body2">
        {videoCall.end_date
          ? `${findDifferenceInMinutes(videoCall.end_date, videoCall.start_date)} minutes`
          : videoCall.actual_time
            ? `${videoCall.actual_time} minutes`
            : videoCall.call_duration
              ? `${videoCall.call_duration} minutes`
              : "-"}
      </Typography>
    </TableCell>
    <TableCell align="left" sx={{ p: 5 }}>
      <Typography variant="body2">
        {videoCall.billing_type ? capitalizeBillingTypeFirstLetter(videoCall.billing_type) : "-"}
      </Typography>
    </TableCell>
    <TableCell align="left" sx={{ p: 5 }}>
      <Link
        component="button"
        variant="body2"
        underline="hover"
        onClick={userPermissions?.view_video_call_details ? onViewDetails : undefined}
        sx={{
          color: "primary.main",
          cursor: userPermissions?.view_video_call_details ? "pointer" : "default",
          opacity: userPermissions?.view_video_call_details ? 1 : 0.5,
        }}
        tabIndex={userPermissions?.view_video_call_details ? 0 : -1}
        aria-disabled={!userPermissions?.view_video_call_details}
        aria-label="View call details"
      >
        View
      </Link>
    </TableCell>
    <TableCell align="left" sx={{ p: 5 }}>
      <IconButton size="small" onClick={onOpenMenu} sx={{ color: "text.secondary" }}>
        <DotsThree size={20} weight="bold" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {currentTab === "past" ? (
          <>
            <MenuItem
              onClick={onDownloadNotes}
              disabled={!videoCall.end_date || !userPermissions?.download_session_notes}
            >
              <ListItemIcon>
                <DownloadSimple size={18} />
              </ListItemIcon>
              <ListItemText>Download Notes</ListItemText>
            </MenuItem>
            {videoCall.billing_type !== "IN_PERSON_ASSESSMENT" && (
              <MenuItem
                onClick={onWatchVideo}
                disabled={
                  !videoCall.end_date ||
                  !videoCall.video_key ||
                  !userPermissions?.view_client_video_session
                }
              >
                <ListItemIcon>
                  <Play size={18} />
                </ListItemIcon>
                <ListItemText>Watch Video Call</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={onOpenDataSheet} disabled={onOpenDataSheet == null}>
              <ListItemIcon>
                <Target size={18} />
              </ListItemIcon>
              <ListItemText>Open Data Sheet</ListItemText>
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem
              onClick={onReschedule}
              disabled={
                !userPermissions?.reschedule_call || !(canModifyEvent || currentUserAssignedToCall)
              }
            >
              <ListItemIcon>
                <CalendarDots size={18} />
              </ListItemIcon>
              <ListItemText>Reschedule</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={onCancel}
              disabled={
                !userPermissions?.cancel_call || !(canModifyEvent || currentUserAssignedToCall)
              }
            >
              <ListItemIcon>
                <CalendarX size={18} />
              </ListItemIcon>
              <ListItemText>Cancel</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </TableCell>
  </TableRow>
);
